import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from '@mui/material/Skeleton'
import Button from '@mui/material/Button'
import EditIcon from '@mui/icons-material/Edit'
import Box from '@mui/material/Box'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import CameraAlt from '@mui/icons-material/CameraAlt'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import OutputIcon from '@mui/icons-material/Output'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import Divider from '@mui/material/Divider'
import { formatEventTimeColumn, formatLocalDateTime } from '@/utils/date'
import { useDispatch, useSelector } from 'react-redux'
import { 
  selectPlateNumberHistoryParams, 
  selectShowPlateNumberHistory,
  setPlateNumberEditParams 
} from '../modalsSlice'
import { useGetChannelsByBuildingQuery } from '@/api'
import { useGetLastEntriesQuery } from '@/api/adminServiceApi'
import { type PhotoNumberQueryArg } from '@/api/enterance'
import useEntryType from '@/pages/main/components/races/components/EnteranceEvents/useEntryType'
import {
  selectChannelIdToDirectionMap,
  selectChannelIdToNameMap,
} from '@/features/channels/selectors'
import ChannelsFilterSelect, {
  getEffectiveChannelIds,
  type ChannelId,
} from '@/components/ChannelsFilterSelect'

interface PlateNumberHistoryContentProps {
  onPhotoClick: (id: PhotoNumberQueryArg) => void
}

const PlateNumberHistoryContent = ({
  onPhotoClick,
}: PlateNumberHistoryContentProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const show = useSelector(selectShowPlateNumberHistory)
  const modalParams = useSelector(selectPlateNumberHistoryParams);
  const channelIdToNameMap = useSelector(selectChannelIdToNameMap);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedChannelIds, setSelectedChannelIds] = useState<ChannelId[]>(
    Array.isArray(modalParams?.channelIds) ? modalParams.channelIds : []
  );
  const buildingId = modalParams?.buildingId;
  const { data: channelsData = [] } = useGetChannelsByBuildingQuery(
    buildingId != null ? +buildingId : undefined,
    { skip: !show || buildingId == null }
  )

  useEffect(() => {
    if (!show) return
    setSelectedChannelIds(
      Array.isArray(modalParams?.channelIds) ? modalParams.channelIds : []
    )
    setPageNumber(1)
  }, [show, modalParams])

  const channelIds = useMemo(
    () => getEffectiveChannelIds(selectedChannelIds, channelsData),
    [selectedChannelIds, channelsData]
  )

  const handleChannelIdsChange = (nextChannelIds: ChannelId[]) => {
    setSelectedChannelIds(nextChannelIds)
    setPageNumber(1)
  }

  const queryParams = {
    buildingId: modalParams?.buildingId,
    search: modalParams?.plateNumber,
    channelIds,
    size: 10,
    page: pageNumber,
  }
  const {
    data: currentData,
    isFetching,
    isLoading,
  } = useGetLastEntriesQuery(queryParams, {
    skip: !modalParams?.plateNumber || !show,
  })
  const currentPage = currentData?.data ?? [];
  const totalItems = Object.values(currentData?.metadata?.channels ?? {}).reduce((acc, curr) => acc + curr.total_count, 0);
  const totalPages = Math.ceil(totalItems / queryParams.size);
  const currentItemsRange = queryParams.size*(pageNumber-1) + 1 
    + ' - ' 
    + Math.min(queryParams.size*pageNumber, totalItems);
  const hasNextPage = (currentPage?.length ?? 0) >= queryParams.size;
  const hasPreviousPage = pageNumber > 1;

  const onPreviousPageClick = () => {
    if (!hasPreviousPage || isLoading) return;
    setPageNumber(pageNumber - 1);
  }

  const onNextPageClick = () => {
    if (!hasNextPage || isLoading) return;
    setPageNumber(pageNumber + 1);
  }
  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pb: 1.5,
          gap: 2,
        }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            minWidth: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('history_of')}
          </Typography>
          <PlateNumberBadge plateNumber={modalParams?.plateNumber} />
        </Stack>
        <ChannelsFilterSelect
          channelsList={channelsData}
          value={selectedChannelIds}
          onChange={handleChannelIdsChange}
          labelId="plate-number-history-channels-label"
        />
      </Stack>
      <DialogContent 
        dividers
        sx={{
          p: 0,
        }}
      >
        <Stack
          spacing={1.25}
          sx={{
            p: 1.5,
          }}
        >
          {isFetching && <HistoryCardsSkeleton count={4} />}
          {!isFetching && currentPage.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                py: 2,
                textAlign: 'center'
              }}>
              {t('no_data')}
            </Typography>
          )}
          {!isFetching && currentPage.map((entry) => (
            <HistoryFeedItem
              key={entry.id}
              entry={entry}
              channelName={channelIdToNameMap[entry.channel_id]}
              onPhotoClick={() => onPhotoClick(entry.id)}
              onEditClick={() => dispatch(setPlateNumberEditParams({
                entryId: entry?.id,
                plateNumber: entry?.plate_number
              }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "space-between",
        }}
      >
        <Box 
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          {
          hasPreviousPage && (
            <Button
              variant="outlined"
              loading={isLoading}
              disabled={isLoading}
              onClick={onPreviousPageClick}
            >
              {t('back')}
            </Button>
          )}
        </Box>
        <Box 
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            textAlign: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography 
            variant="caption"
          >
            {currentItemsRange}. {t('total')}: {totalItems}
          </Typography>
          <Typography 
            variant="caption"
          >
            {t('page')}: {pageNumber} {t('out_of')} {totalPages}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            textAlign: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {
            hasNextPage && (
              <Button
                variant="outlined"
                loading={isLoading}
                disabled={isLoading}
                onClick={onNextPageClick}
              >
                {t('next')}
              </Button>
            )
          }
        </Box>
      </DialogActions>
    </>
  );
}

const HistoryFeedItem = ({ entry, channelName, onPhotoClick, onEditClick }) => {
  const { t } = useTranslation()
  const { entryTypeTranslationMap, entryTypeStatus } = useEntryType()
  const channelIdToDirectionMap = useSelector(selectChannelIdToDirectionMap);
  const direction = channelIdToDirectionMap[entry.channel_id];
  const directionColorNameMap = {
    in: 'warning.main',
    out: 'highlight.main',
  }
  const directionIconMap = {
    in: <ExitToAppIcon fontSize="small" color="inherit" />,
    out: <OutputIcon fontSize="small" color="inherit" sx={{ transform: 'rotate(180deg)' }} />,
  }
  const directionTranslationMap = useMemo(() => ({
    in: t('entrance-type_in'),
    out: t('entrance-type_out'),
  }), [t])
  const entryTypeColor = entryTypeStatus(entry.type) === 'good' ? 'success' : 'error';
  const entryCardBorderLeftColor = (direction !== 'in')
    ? entryTypeColor + '.main'
    : directionColorNameMap[direction];

  return (
    <Paper
      elevation={2}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderLeftWidth: 4,
        borderLeftColor: entryCardBorderLeftColor,
        borderRadius: 2,
        transition: 'background 0.2s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem/>}
        spacing={2}
      >
        <Box sx={{ flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              minWidth: 0
            }}>
            <Chip
              size="small"
              icon={directionIconMap[direction]}
              label={directionTranslationMap[direction] || '-'}
              sx={{
                borderColor: directionColorNameMap[direction],
                color: 'text.primary',
              }}
              variant="outlined"
            />
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "center",
                minWidth: 0
              }}>
              <VideocamOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: "text.secondary",
                  fontWeight: 600
                }}>
                {channelName || `${t('camera')} #${entry.channel_id}`}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center"
            }}
          >
            <Tooltip title={formatLocalDateTime(entry.created_at)} placement="top">
              <Chip
                size="small"
                label={formatEventTimeColumn(entry.created_at)}
                variant="outlined"
                sx={{
                  fontWeight: 600,
                  borderColor: 'text.primary',
                }}
              />
            </Tooltip>
            <Typography variant="caption" sx={{
              color: "text.disabled"
            }}>
              ·
            </Typography>
            <Chip
              size="small"
              label={entryTypeTranslationMap[entry.type] || entry.type}
              sx={{
                backgroundColor: entryTypeColor + '.main',
                color: '#FAFAFA',
              }}
            />
            <Chip
              size="small"
              label={entry.gate_opened ? t('its_open') : t('its_closed')}
              sx={{
                backgroundColor: entry.gate_opened ? 'success.main' : 'error.main',
                color: '#FAFAFA',
              }}
            />
          </Stack>    
        </Box>
        <Stack
          direction="column"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            flexShrink: 1
          }}>
          <Tooltip title={t('photo')}>
            <IconButton
              size="small"
              aria-label="photo"
              onClick={onPhotoClick}
              sx={{ p: 0.5 }}
            >
              <CameraAlt sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('edit')}>
            <IconButton
              size="small"
              aria-label="edit"
              onClick={onEditClick}
              sx={{ p: 0.5 }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
}

const PlateNumberBadge = ({ plateNumber }) => (
  <Box
    sx={{
      height: 32,
      border: '1px solid',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      px: 1.5,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      {plateNumber}
    </Typography>
  </Box>
)

const HistoryCardsSkeleton = ({ count }) => (
  Array.from({ length: count }).map((_, index) => (
    <Paper
      key={index}
      elevation={0}
      sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
    >
      <Skeleton width="40%" height={16} sx={{ mb: 1 }} />
      <Stack direction="row" spacing={1}>
        <Skeleton width={72} height={28} />
        <Skeleton width={88} height={28} />
        <Skeleton width={64} height={28} />
      </Stack>
    </Paper>
  ))
)

export default PlateNumberHistoryContent
